/**
 * Animated vinyl record disc.
 * Travels from sleeve → platter via Framer Motion x/scale transitions.
 * Renders concentric groove rings, a light-reflection gradient, and a
 * center label with optional album art. Click coordinates are emitted to parent.
 */
import { motion } from "framer-motion";

interface VinylRecordProps {
  albumCover?: string;
  position: 'sleeve' | 'traveling' | 'platter';
  isVisible: boolean;
  onVinylClick?: (x: number, y: number) => void;
}

const VinylRecord = ({ albumCover, position, isVisible, onVinylClick }: VinylRecordProps) => {
  if (!isVisible) return null;

  const getPositionStyles = () => {
    switch (position) {
      case 'sleeve':
        return { x: '-140%', scale: 1.35 };
      case 'traveling':
        return { x: '-60%', scale: 1.15 };
      case 'platter':
        return { x: 0, scale: 1 };
      default:
        return { x: 0, scale: 1 };
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onVinylClick?.(x, y);
  };

  return (
    <motion.div
      className="absolute inset-0 rounded-full cursor-pointer"
      initial={{ x: '-140%', scale: 1.35 }}
      animate={getPositionStyles()}
      transition={{
        duration: 1,
        ease: [0.16, 1, 0.3, 1],
      }}
      onClick={handleClick}
    >
      {/* Main vinyl disc */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[hsl(0,0%,10%)] to-[hsl(0,0%,4%)] shadow-xl">
        {/* Outer edge highlight */}
        <div className="absolute inset-0 rounded-full border border-[rgba(255,255,255,0.08)]" />

        {/* Groove pattern - multiple concentric rings */}
        <div className="absolute inset-[8%] rounded-full vinyl-grooves">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full border border-[rgba(255,255,255,0.02)]"
              style={{ inset: `${5 + i * 6}%` }}
            />
          ))}
        </div>

        {/* Light reflection - sweeping highlight */}
        <motion.div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.06) 45%, transparent 60%)',
          }}
        />

        {/* Center label */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[35%] h-[35%] rounded-full overflow-hidden shadow-inner">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(0,0%,15%)] to-[hsl(0,0%,10%)]" />
          {albumCover && (
            <img
              src={albumCover}
              alt="Album label"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[127%] h-[127%] object-cover grayscale opacity-60"
            />
          )}
          <div className="absolute inset-[15%] rounded-full border border-[rgba(255,255,255,0.1)]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[15%] h-[15%] rounded-full bg-[hsl(0,0%,5%)]" />
        </div>

        {/* Additional subtle shine */}
        <div 
          className="absolute inset-0 rounded-full opacity-30 pointer-events-none"
          style={{
            background: 'conic-gradient(from 0deg, transparent 0deg, rgba(255,255,255,0.02) 10deg, transparent 20deg, transparent 180deg, rgba(255,255,255,0.02) 190deg, transparent 200deg)',
          }}
        />
      </div>
    </motion.div>
  );
};

export default VinylRecord;
